import { useMutation } from '@apollo/client/react';
import { CardFlavours } from '@components/Card2/types';
import { cardEditContext } from '@context/card-edit-context';
import { CardListPropertyDetailsFragmentDoc } from '@graphql/fragments/cards/list-property.graphql';
import {
  CreateCardListPropertyDocument,
  RemoveCardListPropertyDocument,
  UpdateCardListPropertyDocument,
} from '@graphql/mutations/cards/list-property.graphql';
import { cardClaimContext } from 'app-next/claim/card/contexts';
import { useContext } from 'react';

import type {
  CardAttributeType,
  CreateCardListPropertyInput,
  UpdateCardListPropertyInput,
} from '.cache/__types__';

export const useCardListPropertyMutations = (
  cardId: string,
): {
  create: (input: CreateCardListPropertyInput, attributeType: CardAttributeType) => void;
  update: (input: UpdateCardListPropertyInput, attributeType: CardAttributeType) => void;
  remove: (cardId: string, propertyId: string, propertyName: string | undefined) => void;
} => {
  const [createCardListPropertyMutation] = useMutation(CreateCardListPropertyDocument);
  const [updateCardListPropertyMutation] = useMutation(UpdateCardListPropertyDocument);
  const [removeCardListPropertyMutation] = useMutation(RemoveCardListPropertyDocument);

  const { flavour } = useContext(cardEditContext);

  const { claimCardToken } = useContext(cardClaimContext);

  /* 
    If we're running mutations during the card claim flow,
    then we need to override the authorization headers to use the
    Claim Card Token.
  */
  const mutationContext = claimCardToken
    ? {
        headers: {
          authorization: `ClaimCard ${claimCardToken}`,
        },
      }
    : undefined;

  function handleCreate(input: CreateCardListPropertyInput, attributeType: CardAttributeType) {
    createCardListPropertyMutation({
      context: mutationContext,
      variables: {
        input,
      },
      optimisticResponse: {
        createCardListProperty: {
          __typename: 'CreateCardListPropertyPayload',
          list_property: {
            __typename: 'CardListProperty',
            id: 'temp-id',
            attribute_type: attributeType,
            stage: null,
            ...input,
            // this needs to be after the ...input, so that whatever the input provides gets replaced. This value shouldn't really be changed by the client anyway.
            // TODO: remove from the mutation.
            template_id: null,
          },
        },
      },
      update: (cache, { data }) => {
        const newField = data?.createCardListProperty;
        if (!newField || !cardId) return;

        const propertyName = data?.createCardListProperty?.list_property.attribute_type?.name;
        if (!propertyName) return;

        const newCardListPropertyRef = cache.writeFragment({
          // We must prefix this id with the string 'CardField:' otherwise the resulting cache ref
          // won't actually point to the right place
          id: `CardListProperty:${data.createCardListProperty?.list_property.id}`,
          data: data.createCardListProperty?.list_property,
          fragment: CardListPropertyDetailsFragmentDoc,
          fragmentName: 'cardListPropertyDetails',
        });

        cache.modify({
          id: cache.identify({ __typename: 'Card', id: cardId }),
          fields: {
            properties(existingCardListPropertyRefs) {
              return {
                ...existingCardListPropertyRefs,
                [propertyName]: newCardListPropertyRef,
              };
            },
          },
        });
      },
    });
  }

  function handleUpdate(input: UpdateCardListPropertyInput, attributeType: CardAttributeType) {
    updateCardListPropertyMutation({
      context: mutationContext,
      variables: {
        input,
      },
      optimisticResponse: {
        updateCardListProperty: {
          __typename: 'UpdateCardListPropertyPayload',
          list_property: {
            __typename: 'CardListProperty',
            // Have to set these to null in case they're not provided by the input
            ...input,
            attribute_type: attributeType,
          },
        },
      },
    });
  }

  function handleRemove(cardId: string, propertyId: string, propertyName: string | undefined) {
    removeCardListPropertyMutation({
      context: mutationContext,
      variables: {
        input: {
          id: propertyId,
          card_id: cardId,
        },
      },
      optimisticResponse: {
        removeCardListProperty: {
          id: propertyId,
        },
      },
      update: (cache) => {
        if (!propertyName) {
          return;
        }

        cache.modify({
          id: cache.identify({ __typename: 'Card', id: cardId }),
          fields: {
            // Remove the deleted property from the cache via the card object.
            properties(existingCardProperties) {
              return {
                ...existingCardProperties,
                [propertyName]: null,
              };
            },
          },
        });

        // This cleans up the de-referenced card field from the cache
        cache.gc();
      },
    });
  }

  return {
    create: handleCreate,
    update: handleUpdate,
    remove: handleRemove,
  };
};
